import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Paper,
  Alert,
  Grid,
  Chip,
  Divider,
  TextField,
  Button,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';

const defaultBrandingAssets = [
  { key: 'badge', label: 'Circular Badge' },
  { key: 'mug', label: 'Dive-In Mug' },
  { key: 'pen', label: 'Pen' },
];

const BrandingAssets = ({
  selectedEmployee,
  onStepComplete,
  onStepIncomplete,
  isCompleted,
}) => {
  const { getAuthHeaders } = useAuth();
  const [brandingData, setBrandingData] = useState(null);
  const [newAssetName, setNewAssetName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const employeeId = selectedEmployee?.employeeId;

  useEffect(() => {
    if (employeeId) {
      fetchAssets();
    }
  }, [employeeId]);

  const fetchAssets = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API_URL}/branding-assets/assets/${employeeId}`,
        { headers: getAuthHeaders() }
      );
      setBrandingData(res.data.data);
      
    } catch (err) {
      setError('Failed to fetch branding assets');
    } finally {
      setLoading(false);
    }
  };

  // const checkCompletion = (data) => {
  //   const builtInComplete = defaultBrandingAssets.every(
  //     (item) => data.assets[item.key]?.provided
  //   );
  //   const othersComplete = data.assets.otherAssets?.every((a) => a.provided);
  //   builtInComplete && othersComplete ? onStepComplete() : onStepIncomplete();
  // };

  const handleBuiltInChange = async (key, value) => {
    if (!employeeId) return;
    try {
      const res = await axios.put(
        `${API_URL}/branding-assets/assets/${employeeId}/${key}`,
        { provided: value },
        { headers: getAuthHeaders() }
      );
      setBrandingData(res.data.data);
      // checkCompletion(res.data.data);
    } catch {
      setError('Failed to update asset');
    }
  };

  const handleOtherAssetToggle = async (id, value) => {
    if (!employeeId) return;
    try {
      const res = await axios.put(
        `${API_URL}/branding-assets/assets/${employeeId}/otherAssets`,
        {
          action: 'update',
          assetId: id,
          provided: value,
        },
        { headers: getAuthHeaders() }
      );
      setBrandingData(res.data.data);
      // checkCompletion(res.data.data);
    } catch {
      setError('Failed to update other asset');
    }
  };

  const handleAddOtherAsset = async () => {
    if (!newAssetName.trim() || !employeeId) return;
    try {
      const res = await axios.put(
        `${API_URL}/branding-assets/assets/${employeeId}/otherAssets`,
        {
          action: 'add',
          name: newAssetName.trim(),
        },
        { headers: getAuthHeaders() }
      );
      setBrandingData(res.data.data);
      setNewAssetName('');
    } catch {
      setError('Failed to add other asset');
    }
  };

  const handleDeleteOtherAsset = async (id) => {
    if (!employeeId) return;
    try {
      const res = await axios.put(
        `${API_URL}/branding-assets/assets/${employeeId}/otherAssets`,
        {
          action: 'delete',
          assetId: id,
        },
        { headers: getAuthHeaders() }
      );
      setBrandingData(res.data.data);
    } catch {
      setError('Failed to delete asset');
    }
  };

  if (!employeeId) {
    return (
      <Alert severity="info">
        Please select an employee first to manage their branding assets.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Company Branding Assets Management
      </Typography>

      <Paper sx={{ p: 3, mb: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Typography>Loading assets...</Typography>
        ) : (
          <>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Assign items like company mug, badge, and other custom materials.
            </Typography>

            <Grid container spacing={2}>
              {defaultBrandingAssets.map((item) => (
                <Grid item xs={12} md={6} key={item.key}>
                  <Box
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            brandingData?.assets[item.key]?.provided || false
                          }
                          onChange={(e) =>
                            handleBuiltInChange(item.key, e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label={item.label}
                      sx={{ flexGrow: 1 }}
                    />
                    {brandingData?.assets[item.key]?.provided && (
                      <Chip
                        label="Provided"
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Other Branding Assets
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  size="small"
                  label="New Asset Name"
                  value={newAssetName}
                  onChange={(e) => setNewAssetName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddOtherAsset}
                  disabled={!newAssetName.trim()}
                >
                  Add Asset
                </Button>
              </Grid>
            </Grid>

            {brandingData?.assets.otherAssets?.length > 0 &&
              brandingData.assets.otherAssets.map((asset) => (
                <Box
                  key={asset._id}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    p: 2,
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={asset.provided}
                        onChange={(e) =>
                          handleOtherAssetToggle(asset._id, e.target.checked)
                        }
                        color="primary"
                      />
                    }
                    label={asset.name}
                    sx={{ flexGrow: 1 }}
                  />
                  {asset.provided && (
                    <Chip
                      label="Provided"
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ mr: 1 }}
                    />
                  )}
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteOtherAsset(asset._id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}

            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isCompleted}
                    onChange={(e) =>
                      e.target.checked ? onStepComplete() : onStepIncomplete()
                    }
                    color="primary"
                  />
                }
                label="Mark as Complete"
              />
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default BrandingAssets;
